/**
 * Type definitions for busy time data
 * Import shared types from SchedulingUtils to avoid duplication
 */
import type { BusyTimeBlock, TimeRange } from './SchedulingUtils'
import { isDateInRange } from './SchedulingUtils'

export interface BusyTimeData {
  busy?: BusyTimeBlock[]
  tentative?: BusyTimeBlock[]
  outOfOffice?: BusyTimeBlock[]
  noData?: boolean
  unknown?: boolean
}

/**
 * Check if a time range overlaps with any busy time blocks
 * @param blocks - Array of busy time blocks
 * @param selectionStart - Start of the selected time range
 * @param selectionEnd - End of the selected time range
 * @param isAllDay - Whether the selection is an all-day event
 * @returns true if there is any overlap, false otherwise
 */
const checkBlocksOverlap = (
  blocks: BusyTimeBlock[] | undefined,
  selectionStart: Date,
  selectionEnd: Date,
  isAllDay?: boolean,
): boolean => {
  if (!blocks || !Array.isArray(blocks)) {
    return false
  }

  if (isAllDay) {
    // For all-day events, use date-level comparison
    return blocks.some((block) => {
      return (
        isDateInRange(selectionStart, block.start, block.end) ||
        isDateInRange(selectionEnd, block.start, block.end) ||
        isDateInRange(block.start, selectionStart, selectionEnd)
      )
    })
  }

  return blocks.some((block) => {
    return selectionStart < block.end && selectionEnd > block.start
  })
}

/**
 * Check if attendee has conflict with selected time range
 * @param busyData - Attendee's busy time data
 * @param timeRange - Selected time range { start: Date, end: Date }
 * @param isAllDay - Whether the selection is an all-day event
 * @returns true if there is a conflict, false otherwise
 */
export const hasConflict = (
  busyData: BusyTimeData | null | undefined,
  timeRange: TimeRange | null | undefined,
  isAllDay?: boolean,
): boolean => {
  if (!busyData || !timeRange) {
    return false
  }

  const { start: selectionStart, end: selectionEnd } = timeRange

  // Check busy, tentative, and out-of-office times
  return (
    checkBlocksOverlap(busyData.busy, selectionStart, selectionEnd, isAllDay) ||
    checkBlocksOverlap(busyData.tentative, selectionStart, selectionEnd, isAllDay) ||
    checkBlocksOverlap(busyData.outOfOffice, selectionStart, selectionEnd, isAllDay)
  )
}

/**
 * Check if attendee has conflict with multiple selected time ranges
 * Returns true if there is a conflict with ANY of the time ranges
 *
 * @param busyData - Attendee's busy time data
 * @param timeRanges - Array of selected time ranges
 * @param isAllDay - Whether the selections are all-day events
 * @returns true if there is a conflict with at least one time range, false otherwise
 */
export const hasConflictWithMultipleRanges = (
  busyData: BusyTimeData | null | undefined,
  timeRanges: TimeRange[] | null | undefined,
  isAllDay?: boolean,
): boolean => {
  if (!busyData || !timeRanges || timeRanges.length === 0) {
    return false
  }

  // Check if ANY of the time ranges has a conflict
  return timeRanges.some((timeRange) => hasConflict(busyData, timeRange, isAllDay))
}

/**
 * Check if attendee has any busy times
 * @param busyData - Attendee's busy time data
 * @returns true if attendee has any busy time blocks, false otherwise
 */
export const hasBusyTimes = (busyData: BusyTimeData | null | undefined): boolean => {
  if (!busyData) {
    return false
  }

  return (
    (busyData.busy && busyData.busy.length > 0) ||
    (busyData.tentative && busyData.tentative.length > 0) ||
    (busyData.outOfOffice && busyData.outOfOffice.length > 0) ||
    false
  )
}
